import type {
  Announcement,
  EventLogQuery,
  SignalRNotificationList,
  UserConfigurationItemToEdit
} from 'common/models';

import type { CappedTableResultOfEventLogEntry, ListEventLogsParams } from './models/api';
import type {
  AddressSearchProvider,
  AddressToGeocode,
  Attachment,
  AttachmentToEdit,
  ColorPalette,
  ConfigurationItem,
  GeocodeAddressResult,
  GetFeatureFlagsQueryResult,
  LoggedUserInfo,
  LogInRequest,
  LoginResult,
  PublicCountry,
  PublicPublicCompanyInfo,
  SearchAddressResult
} from './models/mainservice';
import {
  notificationHubClient,
  protectedApiClient,
  protectedApiClientV2
} from './support/baseApiClients';
import { apiClient } from './support/unprotectedBaseApiClients';

export default {
  signIn: (
    // tenantCode: string,
    // username: string,
    // password: string,
    // deviceIdentifier: string,
    // deviceType: 'Desktop' | 'Tablet' | 'Mobile' | 'Unknown'
    params: LogInRequest
  ) => {
    return apiClient.post<LoginResult>(`/public/auth/login`, params);
  },
  signOut: () => {
    return apiClient.post<undefined>('/public/auth/LogOut');
  },
  confirmEmail: async (userId: string, token: string) => {
    const result = await apiClient.put<undefined>(`/loggedUser/emailConfirmation`, {
      userId,
      token
    });
    return result.data;
  },
  getCompanyAndUserInfo: async () => {
    const result = await protectedApiClient.get<LoggedUserInfo>('/LoggedUser/Info');
    return result.data;
  },
  requestEmailConfirmation: () => {
    return protectedApiClient.get<undefined>(`/LoggedUser/EmailConfirmation`);
  },
  getPublicCompanySettings: async (tenantCode: string) => {
    const result = await apiClient.get<PublicPublicCompanyInfo>(`/public/company/${tenantCode}`);
    return result.data;
  },
  getCountries: async () => {
    const result = await protectedApiClient.get<PublicCountry[]>(`/public/country`);
    return result.data;
  },
  getNotificationsHistory: async (params: EventLogQuery) => {
    const result = await notificationHubClient.get<SignalRNotificationList>(`/user/history`, {
      params
    });
    return result.data;
  },
  markNotificationsAsSeen: async (notificationIds: string[]) => {
    const result = await notificationHubClient.put<undefined>(`/user/markAsSeen`, {
      notificationIds
    });
    return result.data;
  },
  searchForAddresses: (searchPhrase: string, provider?: AddressSearchProvider) => {
    return protectedApiClient.get<SearchAddressResult>(`/address/search/v2/${searchPhrase}`, {
      params: { provider }
    });
  },
  geocodeAddress: (addressToGeocode: AddressToGeocode, provider?: AddressSearchProvider) => {
    return protectedApiClient.post<GeocodeAddressResult>(`/address/geocode/v2`, addressToGeocode, {
      params: { provider }
    });
  },
  geocodePlaceId: (placeId: string, provider?: AddressSearchProvider) => {
    return protectedApiClient.get<GeocodeAddressResult>(`/address/geocodePlace/v2/${placeId}`, {
      params: { provider }
    });
  },
  publicSearchForAddresses: (searchPhrase: string, provider?: AddressSearchProvider) => {
    return apiClient.get<SearchAddressResult>(`/public/address/search/v2/${searchPhrase}`, {
      params: { provider }
    });
  },
  publicGeocodeAddress: (addressToGeocode: AddressToGeocode, provider?: AddressSearchProvider) => {
    return apiClient.post<GeocodeAddressResult>(
      `/public/address/Geocode/v2`,

      addressToGeocode,
      { params: provider }
    );
  },
  publicGeocodePlaceId: (placeId: string, provider?: AddressSearchProvider) => {
    return apiClient.get<GeocodeAddressResult>(`/public/address/geocodePlace/v2/${placeId}`, {
      params: { provider }
    });
  },
  getConfiguration: async () => {
    const result = await protectedApiClient.get<ConfigurationItem[]>(
      '/currentCompany/configuration'
    );

    return result.data;
  },
  getUserConfigurationV2: async () => {
    const result = await protectedApiClient.get<ConfigurationItem[]>('/configuration/v2/user');

    return result.data;
  },
  getCompanyConfigurationV2: async () => {
    const result = await protectedApiClient.get<ConfigurationItem[]>('/configuration/v2/company');

    return result.data;
  },
  editConfigurationV2: async (itemToEdit: UserConfigurationItemToEdit) => {
    const result = await protectedApiClient.patch<undefined>(`/configuration/v2`, itemToEdit);

    return result.data;
  },
  editConfiguration: async (configuration: ConfigurationItem) => {
    const result = await protectedApiClient.patch<undefined>(
      '/currentCompany/configuration',
      configuration
    );
    return result.data;
  },
  addTestData: () => {
    return protectedApiClient.post<undefined>('/superadmin/company/testData');
  },
  deleteTestData: () => {
    return protectedApiClient.delete<undefined>('/superadmin/company/testData');
  },
  errorTest: async (errorCode?: number, errorPercentage?: string) => {
    const result = await protectedApiClient.get<undefined>(
      `/test/error/${errorCode || 500}/chance/${errorPercentage || 50}`
    );

    return result.data;
  },
  editAttachment: (attachmentId: string, data: AttachmentToEdit) => {
    return protectedApiClient.put<Attachment>(`/attachment/${attachmentId}`, data);
  },
  assignOrderToAttachment: async (params: { attachmentId: string; orderId: string }) => {
    const result = await protectedApiClient.put<undefined>(
      `/attachment/${params.attachmentId}/assignOrder`,
      { id: params.orderId }
    );

    return result.data;
  },
  assignInterventionToAttachment: async (params: {
    attachmentId: string;
    interventionId: string;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/attachment/${params.attachmentId}/assignIntervention`,
      { id: params.interventionId }
    );

    return result.data;
  },
  assignServiceObjectToAttachment: async (params: {
    attachmentId: string;
    serviceObjectId: string;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/attachment/${params.attachmentId}/assignServiceObject`,
      { id: params.serviceObjectId }
    );

    return result.data;
  },
  deleteAttachment: async (attachmentId: string) => {
    const result = await protectedApiClient.delete<undefined>(`/attachment/${attachmentId}`);

    return result.data;
  },
  getFeatureFlags: async () => {
    const result = await protectedApiClient.get<GetFeatureFlagsQueryResult>('/featureFlag');

    return result.data;
  },
  getCurrentAnnouncements: async () => {
    const result = await protectedApiClient.get<Announcement[]>('/announcements/current');

    return result.data;
  },
  dismissAnnouncement: async (id: string) => {
    const result = await protectedApiClient.post<undefined>(`/announcements/dismiss/${id}`);

    return result.data;
  },
  getColorPalettes: async () => {
    const result = await apiClient.get<ColorPalette[]>('/public/colorPalettes');

    return result.data;
  },
  getEventLog: async (params: ListEventLogsParams) => {
    const result = await protectedApiClientV2.get<CappedTableResultOfEventLogEntry>(
      '/audit/event-logs',
      {
        params
      }
    );

    return result.data;
  }
};
